import React, { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { AiFillPrinter } from 'react-icons/ai';
import { IoIosMail, IoLogoWhatsapp } from 'react-icons/io';
import { MdWifiCalling3 } from 'react-icons/md';
import ShowMoreShowLessText from '../ShowMoreShowLess/ShowMoreShowLessText';
import classes from './AgentIntroSection.module.css';

import { BsDot } from 'react-icons/bs';
import { RiShareForwardFill, RiUserAddLine, RiUserFollowLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { restrictOnlyUserActions } from '../../Helper/restrictActions';
import { capitalizeFirstLetter, imageUrl } from '../../config/apiUrl';
import CompanyOrAgentEmailModal from '../../modals/CompanyOrAgentEmailModal';
import { useFollowUnFollowCompanyOrAgentMutation } from '../../store/apiSlice/apiSlice';
import { updateUser } from '../../store/auth/authSlice';
import BannerImageAd from '../BannerImageAd';
import BreadCrumb from '../BreadCrumb';
import Button from '../Button/Button';
import ShareModal from '../ShareModal/ShareModal';
import Tooltip from '../Tooltip';

const AgentIntroSection = ({ className, data, t, verticalBanner, verticalLink = '' }) => {
  const { selectedLanguage } = useSelector((state) => state.languageReducer);
  const { isLogin, user } = useSelector((state) => state.authReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isShareModalOpen, setIsShareModalOpen] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [onFollow, { isLoading }] = useFollowUnFollowCompanyOrAgentMutation();

  const isFollowed = user?.agentFollowing?.includes(data?._id);

  const handleAction = () => {
    const isAllowedActions = restrictOnlyUserActions(user?.role);
    if (!isLogin) {
      navigate('/login');
    } else if (isAllowedActions) {
      return true;
    }
  };

  async function handleFollow() {
    if (!handleAction()) return;
    const res = await onFollow(data?._id);
    if (res?.data) {
      dispatch(updateUser(res?.data?.data));
      toast.success(
        `${t('agent.agentIntroSection.agent')} ${
          isFollowed
            ? t('agent.agentIntroSection.unFollowed')
            : t('agent.agentIntroSection.followed')
        } ${t('agent.agentIntroSection.successfully')}`
      );
    }
  }

  return (
    <section className={className || ''}>
      <Row className={classes.topRow}>
        <Col sm={12} md={6}>
          <div className={classes?.breadCrumbContainer}>
            <BreadCrumb
              data={[
                { title: 'Home', path: '/' },
                { title: 'Agents', path: '/agents' },
                {
                  title: capitalizeFirstLetter(data?.agentName?.[selectedLanguage]),
                  active: true
                }
              ]}
            />
            <p>{capitalizeFirstLetter(data?.agentName?.[selectedLanguage])}</p>
          </div>
        </Col>
        <Col sm={12} md={6} className={classes?.shareContainer}>
          {/* Share + print */}
          <div className={classes?.shareLogoContainer}>
            <div>
              <AiFillPrinter fill="#a4a4a4" size={25} />
            </div>
            <button className="btnWrapper" onClick={() => setIsShareModalOpen(true)}>
              <RiShareForwardFill fill="#a4a4a4" size={25} />
            </button>
          </div>
        </Col>
      </Row>
      <Row className={classes.row}>
        {/* Basic Intro */}
        <Col sm={12} md={4} lg={3}>
          <div className={classes?.agentIntroContainer}>
            <div className={classes?.profileImageContainer}>
              <img alt="agent" src={imageUrl(data?.photo)} />
            </div>
            <h5>{data?.agentName?.[selectedLanguage]}</h5>
            <p>{data?.agentDesignation?.[0]?.[selectedLanguage]}</p>

            {/* Follow Btn */}
            <Button
              disabled={isLoading}
              className={classes?.followBtnContainer}
              onClick={handleFollow}>
              {(() => {
                if (isLoading) {
                  return t('agent.followBtnLoading');
                } else if (isFollowed) {
                  return (
                    <>
                      <RiUserFollowLine fill="var(--primary-clr)" size={25} />
                      {t('agent.following')}
                    </>
                  );
                } else {
                  return (
                    <>
                      <RiUserAddLine fill="var(--primary-clr)" size={25} />
                      {t('agent.follow')}
                    </>
                  );
                }
              })()}
            </Button>

            <div className={classes.socialIconsWrap1}>
              <div className={classes.socialIcons1}>
                <Tooltip text={t('agent.tooltips.call')} href={`tel:${data?.phoneNumber}`}>
                  <MdWifiCalling3 fill="var(--primary-clr)" size={30} />
                </Tooltip>
              </div>
              <button
                className={['btnWrapper', classes.socialIcons1, classes.midIcon].join(' ')}
                onClick={() => setIsModalOpen(true)}>
                <Tooltip text={t('agent.tooltips.email')}>
                  <IoIosMail fill="var(--primary-clr)" size={32} />
                </Tooltip>
              </button>
              <div className={classes.socialIcons1}>
                <Tooltip
                  text={t('agent.tooltips.whatsapp')}
                  href={`https://wa.me/${data?.whatsapp}`}
                  target={'_blank'}>
                  <IoLogoWhatsapp fill="var(--primary-clr)" size={30} />
                </Tooltip>
              </div>
            </div>
          </div>
        </Col>

        {/* About Me */}
        <Col sm={12} md={8} lg={7}>
          <div className={classes?.aboutMeContainer}>
            {/* company logo + share */}
            <div className={classes?.companyLogoAndShareContainer}>
              <div className={classes?.companyInfo}>
                <button
                  className={['btnWrapper', classes?.companyLogoContainer].join(' ')}
                  onClick={() => navigate(`/company/${data?.company?.slug}`)}>
                  <img alt="company" src={imageUrl(data?.company?.photo)} />
                </button>
                <div className={classes?.companyNameContainer}>
                  <h6>{data?.company?.companyName?.[selectedLanguage]}</h6>
                  <p>{data?.company?.companyType?.[selectedLanguage]}</p>
                </div>
              </div>
            </div>

            {/* Agent Details */}
            <div className={classes?.agentDetailsContainer}>
              <Row className={classes?.agentDetails}>
                <Col md={3}>{t('agent.weSpeak')} </Col>
                <Col md={9}>{data?.language?.map((e) => e?.[selectedLanguage])?.join(', ')}</Col>
              </Row>
              <Row className={classes?.agentDetails}>
                <Col md={3}>{t('agent.properties')}</Col>
                <Col md={9}>
                  <div className={classes?.propertyMain}>
                    <div className={classes.propertyItem}>
                      <span>({data?.buyProperties ?? 0})</span> {t('agent.forBuy')}
                    </div>
                    <BsDot size={18} />
                    <div className={classes.propertyItem}>
                      <span>({data?.rentProperties ?? 0})</span> {t('agent.forRent')}
                    </div>
                    <BsDot size={18} />
                    <div className={classes.propertyItem}>
                      <span>({data?.totalProjects ?? 0})</span> {t('agent.projects')}
                    </div>
                    <BsDot size={18} />
                    <div className={classes.propertyItem}>
                      <span>({data?.totalEvents ?? 0})</span> {t('agent.events')}
                    </div>
                  </div>
                </Col>
              </Row>
              <Row className={classes?.agentDetails}>
                <Col md={3} className={classes.serviceCol}>
                  {`${t('agent.serviceAreas')}:`}{' '}
                </Col>
                <Col md={9}>{data?.serviceAreas?.[selectedLanguage]}</Col>
              </Row>
              <Row className={classes?.agentDetails}>
                <Col md={3}>{`${t('agent.designation')}:`} </Col>
                <Col md={9}>
                  {data?.agentDesignation?.map((e) => e?.[selectedLanguage])?.join(', ')}
                </Col>
              </Row>
            </div>

            {/* Description */}
            <div className={classes?.descriptionContainer}>
              <h5>
                {t('agent.aboutMe')} {data?.agentName?.[selectedLanguage]}
              </h5>
              <ShowMoreShowLessText text={data?.description?.[selectedLanguage]} visibility={200} />
            </div>
          </div>
        </Col>

        {/* Banner Ad */}
        {verticalBanner && (
          <Col sm={12} md={12} lg={2}>
            <div className={classes?.bannerAdContainer}>
              <BannerImageAd
                link={verticalLink}
                image={verticalBanner}
                className={classes.banner}
              />
            </div>
          </Col>
        )}
      </Row>

      {isShareModalOpen && <ShareModal show={isShareModalOpen} setShow={setIsShareModalOpen} />}
      {isModalOpen && (
        <CompanyOrAgentEmailModal
          t={t}
          show={isModalOpen}
          setShow={setIsModalOpen}
          type="agent"
          data={data}
        />
      )}
    </section>
  );
};

export default AgentIntroSection;
